<template>
  <div class="flex items-center gap-1">
    <svg ref="chart" :class="[ 'block', isLoading && 'opacity-50' ]"></svg>

    <div v-if="isLoading" class="flex w-12.5 shrink-0 flex-col gap-1">
      <ui-skeleton class="h-4 w-7" />
      <ui-skeleton class="h-2.5 w-11.5 rounded-xs" />
    </div>
    <div v-else class="flex w-12.5 shrink-0 flex-col leading-tight">
      <span class="text-[13px] font-black text-foreground">{{ formatMinimalNumber(total) }}</span>
      <span class="line-clamp-2 text-[9px] text-muted-foreground">{{ label }}</span>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

import { formatMinimalNumber } from '@/utils/number';

export default {
  name: 'AnalyticsChart',
  props: {
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 200,
    },
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    total : {
      type: Number,
      required: false,
      default: 0,
    },
    strokeWidth: {
      type: Number,
      default: 5,
    },
    padding: {
      type: Number,
      default: 10,
    },
    label: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      svg: null,
      xScale: null,
      yScale: null,
      loadingTimer: null,
      loadingCounter: 0,
    };
  },
  computed: {
    curve() {
      if (this.isLoading) {
        return 'curveCatmullRom';
      }

      return 'curveMonotoneX';
    },
    computedData() {
      if (!this.isLoading) {
        return this.data;
      }

      const frequency = 0.3;
      const speed = 0.075;

      const loadingData = [];
      for (let i = 0; i < 30; ++i) {
        loadingData.push({
          x: i,
          y: Math.sin(frequency * i + speed * this.loadingCounter),
        });
      }

      return loadingData;
    },
  },
  watch: {
    isLoading: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const fps = 60;
          this.loadingTimer = setInterval(() => ++this.loadingCounter, 1000 / fps);
        }
        else {
          if (this.loadingTimer) {
            clearInterval(this.loadingTimer);
            this.loadingTimer = null;
          }
        }
      },
    },
    computedData: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    width() {
      this.initChart();
      this.renderChart();
    },
    height() {
      this.initChart();
      this.renderChart();
    },
  },
  mounted() {
    this.initChart();
    this.renderChart();
  },
  methods: {
    formatMinimalNumber,
    initChart() {
      d3.select(this.$refs.chart).selectAll('*').remove();

      this.svg = d3.select(this.$refs.chart)
        .attr('width', this.width)
        .attr('height', this.height)
        .attr('viewBox', [ 0, 0, this.width, this.height ])
        .style('max-width', '100%')
        .style('height', 'auto');
    },

    setupScales() {
      if (!this.computedData || this.computedData.length === 0) {
        this.xScale = d3.scaleLinear().range([ 0, this.width ]);
        this.yScale = d3.scaleLinear().range([ this.height, 0 ]);
        return;
      }

      const firstXValue = this.computedData[0].x;
      const isDate = firstXValue instanceof Date || typeof firstXValue === 'string';

      if (isDate) {
        const xExtent = d3.extent(this.computedData, d => new Date(d.x));
        this.xScale = d3.scaleTime()
          .domain(xExtent)
          .range([ this.padding, this.width - this.padding ]);
      }
      else {
        const xExtent = d3.extent(this.computedData, d => d.x);
        this.xScale = d3.scaleLinear()
          .domain(xExtent)
          .range([ this.padding, this.width - this.padding ]);
      }

      let yExtent = d3.extent(this.computedData, d => d.y);
      let yRange = yExtent[1] - yExtent[0];
      if (yRange === 0) {
        yExtent = [ 0, yExtent[1] + 1 ];
        yRange = 1;
      }

      this.yScale = d3.scaleLinear()
        .domain([ yExtent[0], yExtent[1] ])
        .range([ this.height - this.padding, this.padding ]);
    },

    renderChart() {
      if (!this.computedData || this.computedData.length === 0) {
        this.svg.selectAll('*').remove();
        return;
      }

      this.setupScales();

      this.svg.selectAll('*').remove();

      const processedData = this.computedData.map(d => ({
        x: d.x instanceof Date ? d.x : (typeof d.x === 'string' ? new Date(d.x) : d.x),
        y: d.y,
      }));

      const line = d3.line()
        .x(d => this.xScale(d.x))
        .y(d => this.yScale(d.y))
        .curve(d3[this.curve]);

      const area = d3.area()
        .x(d => this.xScale(d.x))
        .y0(this.height - this.padding)
        .y1(d => this.yScale(d.y))
        .curve(d3[this.curve]);

      this.svg.append('path')
        .datum(processedData)
        .style('fill', 'color-mix(in oklab, var(--primary) 10%, transparent)')
        .attr('d', area);

      this.svg.append('path')
        .datum(processedData)
        .attr('fill', 'none')
        .style('stroke', 'var(--primary)')
        .attr('stroke-width', this.strokeWidth)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', line);
    },
  },
};
</script>
